import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Search, Calendar, User, ArrowRight, Loader2, ArrowLeft } from 'lucide-react';
import api, { resolveImageUrl } from '../lib/api';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Input } from '../components/ui/input';
import Pagination from '../components/Pagination';

const BlogListPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [category, setCategory] = useState('Semua');
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 9;

  useEffect(() => {
    document.title = 'Blog & Artikel — Werdi Media';
    window.scrollTo(0, 0);
    api
      .get('/blog', { params: { limit: 500 } })
      .then((r) => setItems(r.data))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  const categories = useMemo(
    () => ['Semua', ...Array.from(new Set(items.map((r) => r.category)))],
    [items]
  );

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    return items.filter((r) => {
      const matchQ =
        !query ||
        r.title.toLowerCase().includes(query) ||
        (r.excerpt || '').toLowerCase().includes(query) ||
        (r.category || '').toLowerCase().includes(query) ||
        (r.author || '').toLowerCase().includes(query);
      const matchC = category === 'Semua' || r.category === category;
      return matchQ && matchC;
    });
  }, [items, q, category]);

  useEffect(() => {
    setPage(1);
  }, [q, category]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paged = useMemo(
    () => filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE),
    [filtered, currentPage]
  );

  const goTo = (p) => {
    setPage(p);
    window.scrollTo({ top: 300, behavior: 'smooth' });
  };

  const fmtDate = (iso) => {
    if (!iso) return '';
    try {
      return new Date(iso).toLocaleDateString('id-ID', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      });
    } catch {
      return iso;
    }
  };

  return (
    <div className="App bg-[#0a0a0a] min-h-screen">
      <Navbar />

      <section className="relative pt-32 pb-12 lg:pt-40 lg:pb-16 overflow-hidden">
        <div className="absolute top-0 -left-32 w-[500px] h-[500px] rounded-full bg-[#D4A94F]/10 blur-[120px] -z-10" />
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-neutral-500 hover:text-[#E9C46A] link-transition mb-6"
          >
            <ArrowLeft className="w-3 h-3" /> Beranda
          </Link>
          <div className="inline-block px-4 py-1.5 rounded-full border border-[#D4A94F]/30 bg-[#D4A94F]/5 mb-5">
            <span className="text-xs font-medium tracking-widest uppercase text-[#E9C46A]">
              Blog & Artikel
            </span>
          </div>
          <h1 className="text-white font-bold text-4xl md:text-6xl leading-[1.05] tracking-tight mb-4">
            Wawasan & <span className="gold-gradient-text">Tips Musisi</span>
          </h1>
          <p className="text-neutral-400 text-lg max-w-2xl mb-10">
            Artikel, tutorial, dan berita terbaru seputar industri musik digital Indonesia.
          </p>

          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1 max-w-xl">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500" />
              <Input
                placeholder="Cari judul, kategori, atau penulis..."
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="pl-11 h-12 rounded-full bg-[#0f0f0f] border-white/10 focus-visible:ring-[#D4A94F] focus-visible:border-[#D4A94F] text-white"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              {categories.map((g) => (
                <button
                  key={g}
                  onClick={() => setCategory(g)}
                  className={`px-4 py-2 rounded-full text-xs font-semibold btn-transition border ${
                    category === g
                      ? 'bg-[#D4A94F] text-black border-[#D4A94F]'
                      : 'bg-transparent text-neutral-300 border-white/10 hover:border-[#D4A94F]/50 hover:text-white'
                  }`}
                >
                  {g}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          {loading ? (
            <div className="flex justify-center py-24">
              <Loader2 className="w-6 h-6 text-[#E9C46A] animate-spin" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-24 text-neutral-500">
              Tidak ada artikel yang cocok. Coba kata kunci lain.
            </div>
          ) : (
            <>
              <p className="text-neutral-500 text-sm mb-6" data-testid="blog-count">
                Menampilkan {(currentPage - 1) * PAGE_SIZE + 1}
                {'–'}
                {Math.min(currentPage * PAGE_SIZE, filtered.length)} dari {filtered.length} artikel
              </p>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="blog-grid">
                {paged.map((p) => (
                  <Link
                    key={p.id}
                    to={`/blog/${p.slug}`}
                    className="group bg-[#0f0f0f] border border-white/[0.06] hover:border-[#D4A94F]/40 rounded-3xl overflow-hidden card-transition hover:-translate-y-1"
                  >
                    <div className="aspect-[16/10] bg-neutral-900 overflow-hidden relative">
                      {p.thumbnail && (
                        <img
                          src={resolveImageUrl(p.thumbnail)}
                          alt={p.title}
                          className="w-full h-full object-cover btn-transition group-hover:scale-105"
                          loading="lazy"
                        />
                      )}
                      <div className="absolute top-3 left-3">
                        <span className="text-[10px] tracking-widest uppercase font-bold text-black bg-[#D4A94F] px-2.5 py-1 rounded-full">
                          {p.category}
                        </span>
                      </div>
                    </div>
                    <div className="p-6">
                      <div className="flex items-center gap-4 text-xs text-neutral-500 mb-3">
                        <span className="flex items-center gap-1.5">
                          <Calendar className="w-3 h-3" /> {fmtDate(p.publish_date)}
                        </span>
                        <span className="flex items-center gap-1.5">
                          <User className="w-3 h-3" /> {p.author}
                        </span>
                      </div>
                      <h3 className="text-white font-bold text-xl leading-tight mb-3 group-hover:text-[#E9C46A] btn-transition">
                        {p.title}
                      </h3>
                      <p className="text-neutral-400 text-sm leading-relaxed line-clamp-3">
                        {p.excerpt}
                      </p>
                      <div className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-[#E9C46A]">
                        Baca Selengkapnya <ArrowRight className="w-3.5 h-3.5" />
                      </div>
                    </div>
                  </Link>
                ))}
              </div>

              {totalPages > 1 && (
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onChange={goTo}
                  testIdPrefix="blog"
                />
              )}
            </>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BlogListPage;
